<template>
  <div ref="pageContainer" class="dcp-cars-srp" :class="[selectedTabClass]" data-test-id="srp">
    <div ref="containerElement" class="srp-grid-layout">
      <div class="dcp-cars-srp__filter-wrapper srp-grid-layout__side-filters">
        <emh-side-filters
          class="dcp-cars-srp__filter"
          :is-loading="isLoading"
          @change-filter="onChangeFilter"
          @clear-all-filters="onClearAllFilters"
          @clear-category="onClearCategory"
          @vehicle-assortment-changed="onAssortmentChanged"
          @close-modal="scrollTop"
        />
      </div>

      <!-- product search result -->
      <div
        id="side-vehicles"
        ref="resultList"
        class="dcp-cars-srp__amount-and-sort dcp-cars-srp-amount-and-sort srp-grid-layout__amount-and-sort"
      >
        <div
          class="dcp-cars-srp__result-amount dcp-cars-srp-result-amount"
          :class="{ 'dcp-cars-srp-result-amount__rtl': isRtl }"
        >
          <span class="dcp-cars-srp-result-amount__number" data-test-id="dcp-cars-srp-result-amount__number">
            {{ totalResultsLimited }}
          </span>
          <span v-if="totalResultsLimited" class="dcp-cars-srp-result-amount__label">
            {{ $t('dcp.srp.cars') }}
          </span>
        </div>
        <div class="dcp-cars-srp__sorting-dropdown">
          <filter-dropdown
            v-if="sortOptions.length"
            :disabled="isLoading"
            :options="sortOptions"
            :value="selectedSort"
            :label="$t('dcp.filtersSearch.sorting')"
            @input="onChangeSortOrder"
          />
        </div>
      </div>
      <testing-generic-wrapper storage-key="SelectedFiltersPill">
        <template #target>
          <selected-filters
            class="dcp-cars-srp__selected-filters srp-grid-layout__selected-filters"
            :filters="filters"
            @change-filter="onChangeFilter"
            @clear-all-filters="onClearAllFilters"
          />
        </template>
      </testing-generic-wrapper>

      <ui-newsletter
        v-if="showAvailabilityNotification"
        v-bind="availabilityNotification"
        class="dcp-cars-srp__availability-notification srp-grid-layout__availability-notification"
        @submit="onSubmitAvailabilityNotification"
      >
        <template #default>
          <ui-newsletter-header>
            {{ $t('srp.availabilityNotification.default.header') }}
          </ui-newsletter-header>
          <ui-newsletter-meta>
            {{ $t('srp.availabilityNotification.default.meta') }}
          </ui-newsletter-meta>
          <ui-newsletter-description>
            {{ $t('srp.availabilityNotification.default.description') }}
          </ui-newsletter-description>
        </template>
        <template #success>
          <ui-newsletter-header>
            {{ $t('srp.availabilityNotification.success.header') }}
          </ui-newsletter-header>
          <ui-newsletter-meta>
            {{ $t('srp.availabilityNotification.success.meta') }}
          </ui-newsletter-meta>
          <ui-newsletter-description>
            {{ $t('srp.availabilityNotification.success.description') }}
          </ui-newsletter-description>
        </template>
      </ui-newsletter>

      <div v-if="searchResults.length > 0" class="dcp-cars-srp__results dcp-cars-srp-results srp-grid-layout__results">
        <template v-for="(item, index) in searchResults" :key="item.code">
          <second-row-slot
            v-if="index === 2 || searchResults.length <= 2"
            v-show="isAuthorMode || hasComponentInSlot"
            id="srpSlot"
            ref="srpSlotRef"
            @slot-manually-inserted="hasComponentInSlot = true"
          ></second-row-slot>
          <div class="dcp-cars-srp-results__tile">
            <product-tile
              :product="item"
              :index="index"
              :is-compare-visible="isCompareEnabled"
              :assortment="item.assortment"
              @popover-open="trackPopoverOpen"
              @tag-expand="trackTagExpand"
              @tag-modal-open="trackTagModalOpen"
            ></product-tile>
          </div>
        </template>
      </div>

      <show-more
        v-if="!isLoading"
        class="dcp-cars-srp__pagination srp-grid-layout__pagination"
        :current-number-of-items="searchResults.length"
        :total-number-of-items="paging.totalResults"
        :total-number-of-results-limited="totalResultsLimited"
        show-more-label="dcp.srp.paging.loadMore"
        current-number-label="dcp.srp.paging.label"
        @load="onLoadMoreProducts"
      >
      </show-more>

      <no-results-message
        v-if="searchResults.length === 0"
        class="dcp-cars-srp-results__no-results-message srp-grid-layout__no-results"
        @clear-all-filters="onClearAllFilters"
      />

      <similar-vehicles
        :vehicles="similarVehicles"
        class="dcp-cars-srp__similar-results srp-grid-layout__similar-results"
      />
    </div>

    <compare-stage v-if="isCompareFeatureEnabled" :modal-title="compareModalTitle" />

    <dealer-selection-modal
      :selected-dealer="dealerFilterInfo"
      :allowed-list="allowedList"
      :dealer-ids="dealerIds"
      :force-selection="forceDealerSelection"
      :headline="$t('dcp.dealerSelection.headline')"
      :description="$t('dcp.dealerSelection.description')"
      :close-on-backdrop-click="false"
      :close-on-escape-key="false"
      @vendor-selected="onVendorSelected"
    />

    <div id="emh-srp-modal-target"></div>
  </div>
</template>

<script lang="ts">
import { IntersectionObserverDirective } from '@dh-io-emh/shared/src/directives/intersectionObserver';
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'SrpView',
  directives: {
    intersectionObserver: IntersectionObserverDirective as any,
  },
});
</script>

<script lang="ts" setup>
import { SMSProduct } from '@dh-io-emh/commerce-api/src/models/shared/ocb';
import { SimilarSearchResultsParams } from '@dh-io-emh/shared/src/api/deep-linking.api';
import { computed, ComputedRef, onMounted, reactive, ref, toRefs, watch } from 'vue';
import { useLocationsFilter } from '@dh-io-emh/search-filter/src/composables/locations-filter.composable';
import TestingGenericWrapper from '@dh-io-emh/shared/src/components/testingGenericWrapper/TestingGenericWrapper.vue';
import DealerSelectionModal from '@dh-io-emh/shared/src/components/dealerSelectionModal/DealerSelectionModal.vue';
import EmhSideFilters from '@dh-io-emh/search-filter/src/components/sideFilters/SideFilters.vue';
import FilterDropdown from '@dh-io-emh/search-filter/src/components/filterDropdown/index.vue';
import {
  getAnalyticsProperty,
  waitForUserInteraction,
} from '@dh-io-emh/shared/src/composables/analytics-ow.composable';
import { useDealerContext } from '@dh-io-emh/shared/src/composables/dealer-context.composable';
import { useNonIndexedFootnotes } from '@dh-io-emh/shared/src/composables/footnotes.composable';
import { useMeta } from '@dh-io-emh/shared/src/composables/meta.composable';
import { useCanonicalLink } from '@dh-io-emh/shared/src/composables/canonical-link.composable';
import { useRouter } from '@dh-io-emh/shared/src/composables/router.composable';
import { useTracking } from '@dh-io-emh/shared/src/composables/tracking.composable';
import { useSrpTracking } from '../../composables/srp-tracking.composable';
import { useUserContext } from '@dh-io-emh/shared/src/composables/user-context.composable';
import { infuse } from '@dh-io-emh/shared/src/ioc-config';
import { Logger } from '@dh-io-emh/shared/src/utils/logger.util';
import { useProductsStore } from '@dh-io-emh/shared/src/stores/products/products.composable';
import { FilterCode } from '@dh-io-emh/shared/src/models/product-filter/product-filter-base.model';
import NoResultsMessage from '../../components/noResultsMessage/index.vue';
import ProductTile from '@dh-io-emh/shared/src/components/productTile/index.vue';
import ShowMore from '../../components/showMore/index.vue';
import { updateQueryParams } from '@dh-io-emh/shared/src/utils/filter-query-params.util';
import { DropdownOption } from '@dh-io-emh/shared/src/stores/products/products.selectors';
import { useFilter } from '@dh-io-emh/search-filter/src/composables/filter.composable';
import { scrollToElement } from '@dh-io-emh/shared/src/utils/scroll.util';
import { useFilterTracking } from '@dh-io-emh/search-filter/src/composables/filter-tracking.composable';
import { getAemEnvironmentVariables } from '@dh-io-emh/shared/src/utils/aem.util';
import SecondRowSlot from '../../components/secondRowSlot/index.vue';
import CompareStage from '@dh-io-emh/shared/src/components/compare/compare-stage/index.vue';
import { useI18n } from '@dh-io-emh/shared/src/composables/i18n.composable';
import { isCompareAvailableAtSomePoint, isCompareAvailableOn } from '@dh-io-emh/shared/src/components/compare/utils';
import UiNewsletter from '@dh-io-emh/ui/src/components/newsletter/Newsletter.vue';
import UiNewsletterHeader from '@dh-io-emh/ui/src/components/newsletter/NewsletterHeader/NewsletterHeader.vue';
import UiNewsletterMeta from '@dh-io-emh/ui/src/components/newsletter/NewsletterMeta/NewsletterMeta.vue';
import UiNewsletterDescription from '@dh-io-emh/ui/src/components/newsletter/NewsletterDescription/NewsletterDescription.vue';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import SelectedFilters from '@dh-io-emh/search-filter/src/components/selectedFiltersPill/SelectedFiltersPill.vue';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import SimilarVehicles from '@dh-io-emh/shared/src/components/similar-vehicles/SimilarVehicles.vue';

const {
  srp: {
    products: { useRtlOnAmountLabel: isRtl },
    enableDynamicPriceFiltering,
  },
  dealerLocator: { forceDealerSelection },
  availabilityNotification: { enabled: isAvailabilityNotificationEnabled },
} = infuse('config');

const { trackLinkClick, getProductsDataTrackingObject } = useTracking();
const { trackResetAllFilters } = useFilterTracking();
const router = useRouter();
const { updateMetaTitleWithKey, updateMetaDescriptionWithKey } = useMeta();
const { setCanonicalLinkForSrp } = useCanonicalLink();
const { dealerContext } = useDealerContext();
const { filters } = useFilter();
const resultList = ref<HTMLElement>();
const pageContainer = ref<HTMLElement>();
const isAuthorMode = ref(false);

useNonIndexedFootnotes('srp', true);

const productsStore = useProductsStore();

const { isLoading, selectedFiltersFormatted, selectedFilters, selectedTab, sortOptions, totalResultsLimited } = toRefs(
  productsStore.selectors,
);

const {
  dealerFilterInfo,
  locationFilterParams,
  selectedCategory,
  results: searchResults,
  selectedSort,
  paging,
} = toRefs(productsStore.state);

const {
  setDealerContext,
  getSearchProducts,
  setFilterValue,
  loadMoreResults,
  setSortOrder,
  resetAllFilters,
  resetCategory,
  setUserContext,
  setCategory,
} = productsStore.dispatchers;

const linkDecorator = ref('');
const containerElement = ref<HTMLDivElement>();
const dealerFilter = computed(() => filters.value.find((filter) => filter.code === FilterCode.locations));
const {
  allowedList,
  dealers,
  vendorSelected: onVendorSelected,
} = useLocationsFilter({
  filter: dealerFilter,
  emit: (name, filter) => onChangeFilter(filter),
});
const hasComponentInSlot = ref(false);
const dealerIds: ComputedRef<Array<string>> = computed(() => dealers.value.map((option) => option.value));
const selectedTabClass = computed(() => `srp-tab-${selectedTab.value}`);
const srpSlotRef = ref();

const { trackPopoverOpen, trackTagExpand, trackTagModalOpen, trackingPageView } = useSrpTracking(
  selectedFilters,
  selectedTab,
  searchResults,
  locationFilterParams,
  dealerFilterInfo,
);

const { t } = useI18n();
const compareModalTitle = t('dcp.srp.compare.modal.title');
const userContext = useUserContext();
const isCompareFeatureEnabled = isCompareAvailableAtSomePoint();
const isCompareEnabled = computed(
  () => isCompareFeatureEnabled && isCompareAvailableOn(selectedTab.value, userContext.value),
);

onMounted(async () => {
  updateMetaDescriptionWithKey('dcp.srp.meta.description');

  const modelFilter = getSingleModelFilter();

  updateSrpMetaTitle(modelFilter);
  setCanonicalLinkForSrp(modelFilter);
  setCarConfiguratorLink();
  trackingPageView();

  const { runMode } = await getAemEnvironmentVariables();

  isAuthorMode.value = runMode?.toLowerCase() === 'author';
});

const updateQueryParameters = (): void => {
  updateQueryParams(router, {
    sort: selectedSort.value,
    assortment: selectedCategory.value,
    filters: filters.value,
  });
};

const scrollTop = () => {
  if (window.scrollY > (pageContainer.value?.offsetTop ?? 0))
    scrollToElement(resultList.value as HTMLElement, { block: 'start', offset: -56 });
};

/**
 * Receive a filter change and direct it to the store
 *
 * @param {object} filter - Subset of filter object including its code and value
 */
const onChangeFilter = ({ filterCode, filterValue }: { filterCode: string; filterValue: Array<number> }) => {
  Logger.debug('SrpView::onChangeFilter()', { filterCode, filterValue });
  setFilterValue({ filterCode, filterValue });

  const modelFilter = getSingleModelFilter();

  updateSrpMetaTitle(modelFilter);
  setCanonicalLinkForSrp(modelFilter);
  updateQueryParameters();
  getSearchProducts();
  scrollTop();
};

/**
 * Change the query if user has selected a different value from the filter dropdown
 */
const onChangeSortOrder = async (sortOrder: DropdownOption) => {
  setSortOrder(sortOrder.value);
  updateQueryParameters();
  await getSearchProducts();

  trackLinkClick({
    eventDetail: {
      category: 'filter',
      action: `sort.${selectedTab.value}`,
      label: `sort.${sortOrder.value}`,
      filter: selectedFiltersFormatted.value.map((filter) => ({
        type: filter.filterType,
        name: filter.filterCode,
        value: filter.filterTrackingValue || filter.filterValue || filter.filterLabel,
      })),
      products: getProductsDataTrackingObject(searchResults.value),
    },
  });
};

const onLoadMoreProducts = async () => {
  await loadMoreResults();

  const { currentPage, pageSize } = productsStore.state.paging;
  const startIndex = currentPage * pageSize;

  trackLinkClick({
    eventDetail: {
      category: 'feature',
      action: 'product_list',
      label: 'show_more',
      products: getProductsDataTrackingObject(searchResults.value.slice(startIndex), startIndex),
    },
  });
};

const onClearAllFilters = () => {
  resetAllFilters();
  updateQueryParameters();
  getSearchProducts();
  trackResetAllFilters();
  scrollTop();
};

const onClearCategory = (codes) => {
  resetCategory(codes);
  updateQueryParameters();
  getSearchProducts();
  scrollTop();
};

const onAssortmentChanged = async (assortment: string) => {
  resetAllFilters();
  setCategory(assortment);
  updateQueryParameters();
  // wait for search api call to complete so that tracking call has latest products data
  await getSearchProducts();
  trackingPageView();
};

const onSubmitAvailabilityNotification = (email: string) => {
  Logger.debug('SrpView::onSubmitAvailabilityNotification()', email);

  trackLinkClick({
    eventDetail: {
      category: 'feature',
      action: 'availability_notification',
      label: 'submit',
      filter: selectedFiltersFormatted.value.map((filter) => ({
        name: filter.filterCode,
        value: filter.filterTrackingValue || filter.filterValue || filter.filterLabel,
      })),
    },
  });

  availabilityNotification.status = 'success';
};

const availabilityNotification = reactive({
  imageUrl: '',
  status: 'none',
});

const showAvailabilityNotification = computed(
  () =>
    isAvailabilityNotificationEnabled && selectedFiltersFormatted.value.length > 0 && searchResults.value.length === 0,
);

const deepLinkingApi = infuse('deepLinkingApi');
const similarVehicles = ref<SMSProduct[]>([]);

const getSimilarVehicles = async () => {
  const filtersParams = selectedFiltersFormatted.value.reduce((acc, val) => {
    if (acc[val.filterCode]) {
      if (Array.isArray(acc[val.filterCode])) {
        acc[val.filterCode].push(val.filterValue);
      } else {
        acc[val.filterCode] = [val.filterValue];
      }
    } else {
      acc[val.filterCode] = val.filterValue;
    }

    return acc;
  }, {});

  const params: SimilarSearchResultsParams = {
    vehicleAssortment: productsStore.state.selectedCategory,
    ...filtersParams,
  };

  Logger.debug('SrpView::getSimilarVehicles', params);

  similarVehicles.value = (await deepLinkingApi.getSimilarSearchResults(params)).products;
};

const setCarConfiguratorLink = async () => {
  try {
    await waitForUserInteraction();
    linkDecorator.value = await getAnalyticsProperty('vars.linkDecorator');
  } catch (error) {
    // don't need to change if there is no link decorator
  }
};

const updateSrpMetaTitle = (modelFilter: string) => {
  if (modelFilter) {
    updateMetaTitleWithKey('dcp.srp.meta.titleWithModelFilter', {
      modelFilter,
    });
  } else {
    updateMetaTitleWithKey('dcp.srp.meta.title');
  }
};

const getSingleModelFilter = (): string => {
  const modelFilters = selectedFilters.value.find(({ code }) => code === 'model');

  return modelFilters?.value.length === 1 ? modelFilters.value[0] : '';
};

watch(userContext, async () => {
  if (enableDynamicPriceFiltering) {
    setUserContext(userContext.value);
    await getSearchProducts();
  }

  trackingPageView();
});

watch(dealerContext, () => {
  setDealerContext(dealerContext.value);
  getSearchProducts();
});

watch(
  searchResults,
  () => {
    if (searchResults.value.length === 0) {
      getSimilarVehicles();
    } else {
      similarVehicles.value = [];
    }
  },
  { immediate: true },
);
</script>

<style src="./srp.scss" lang="scss"></style>
